import React, { useState } from 'react';
import { Loader2, Brain, FileText, TrendingUp, AlertCircle, CheckCircle, Clock, Tag, Sparkles, BarChart3, PieChart as PieChartIcon, RefreshCw } from 'lucide-react';
import { useCaseStatistics, useCaseTypeDistribution, useCaseReasonDistribution, useCasePatternsAI, useCasePatternsSql } from '../hooks/useReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import QueryInfoButton from '../components/QueryInfoButton';
import { CASE_QUERIES } from '../constants/queries';

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#A855F7', '#22C55E', '#E11D48', '#0EA5E9',
  '#D946EF', '#64748B', '#FB923C', '#4ADE80', '#F472B6'
];

const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toLocaleString() || '0';
}

function PatternCard({ pattern, index }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`border rounded-lg p-4 transition-all cursor-pointer hover:shadow-md ${
        index < 3 ? 'border-primary-200 bg-primary-50/30' : 'border-gray-200 bg-white'
      }`}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
            index < 3 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {pattern.rank || index + 1}
          </span>
          <div>
            <h4 className="font-semibold text-gray-900">{pattern.pattern_name}</h4>
            <p className="text-sm text-gray-500 mt-1">{pattern.description}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-gray-900">{formatNumber(pattern.estimated_count)}</div>
          <div className="text-sm text-gray-500">{pattern.percentage}%</div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Severity</span>
              <div className={`mt-1 inline-block px-2 py-1 rounded text-xs font-medium border ${SEVERITY_COLORS[pattern.severity] || SEVERITY_COLORS.medium}`}>
                {pattern.severity?.toUpperCase() || 'MEDIUM'}
              </div>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Category</span>
              <div className="mt-1 text-sm text-gray-900">{pattern.category || 'General'}</div>
            </div>
          </div>
          {pattern.keywords && pattern.keywords.length > 0 && (
            <div className="mt-3">
              <span className="text-xs font-medium text-gray-500 uppercase">Keywords</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {pattern.keywords.map((kw, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                    <Tag className="w-3 h-3 mr-1" />
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CasePatternAnalysis() {
  const [useAI, setUseAI] = useState(false);

  const { data: statsData, isLoading: statsLoading } = useCaseStatistics();
  const { data: typeData, isLoading: typeLoading } = useCaseTypeDistribution();
  const { data: reasonData, isLoading: reasonLoading } = useCaseReasonDistribution();
  const { data: sqlPatternsData, isLoading: sqlLoading } = useCasePatternsSql();
  const { data: aiPatternsData, isLoading: aiLoading, refetch: refetchAI, isFetching: aiFetching } = useCasePatternsAI(useAI);

  const stats = statsData?.data?.[0] || {};
  const typeDistribution = typeData?.data || [];
  const reasonDistribution = reasonData?.data || [];

  // Use AI patterns if available and requested, otherwise use SQL patterns
  const patternsData = useAI && aiPatternsData?.data ? aiPatternsData.data : null;
  const sqlPatterns = sqlPatternsData?.data || [];

  const isLoading = statsLoading || typeLoading || reasonLoading || sqlLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
          <p className="text-gray-500 text-lg">Loading case data...</p>
        </div>
      </div>
    );
  }

  const handleRunAIAnalysis = () => {
    setUseAI(true);
    if (!aiPatternsData) {
      refetchAI();
    }
  };

  const typeChartData = typeDistribution.slice(0, 10).map((t, i) => ({
    name: t.case_type,
    value: parseInt(t.case_count),
    color: COLORS[i % COLORS.length],
  }));

  const reasonChartData = reasonDistribution.slice(0, 10).map((r, i) => ({
    name: r.case_reason,
    count: parseInt(r.case_count),
    color: COLORS[i % COLORS.length],
  }));

  // Build patterns for display
  const displayPatterns = patternsData?.patterns || sqlPatterns.map((p, i) => ({
    rank: i + 1,
    pattern_name: p.pattern_category,
    description: `Cases matching the ${p.pattern_category} pattern based on keyword analysis`,
    estimated_count: parseInt(p.case_count),
    percentage: parseFloat(p.percentage),
    keywords: [p.pattern_category.toLowerCase().replace(/ /g, '_')],
    severity: i < 3 ? 'high' : i < 10 ? 'medium' : 'low',
    category: 'Auto-detected',
  }));

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Brain className="w-7 h-7 text-primary-600" />
            Case Pattern Analysis
          </h2>
          <p className="text-gray-500 mt-1">
            Deep analysis of customer service cases from salesforce.case to identify the top 20 issue patterns
          </p>
        </div>
        <QueryInfoButton
          title="Case Analysis Data Sources"
          queries={[
            CASE_QUERIES.statistics,
            CASE_QUERIES.typeDistribution,
            CASE_QUERIES.reasonDistribution,
            CASE_QUERIES.patternsSql,
          ]}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <FileText className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Cases</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(parseInt(stats.total_cases || 0))}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Closed Cases</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(parseInt(stats.closed_cases || 0))}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Open Cases</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(parseInt(stats.open_cases || 0))}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">High Priority</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(parseInt(stats.high_priority_cases || 0))}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-primary-600" />
            Case Type Distribution
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={typeChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name?.substring(0, 12)}${name?.length > 12 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={true}
                >
                  {typeChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatNumber(value), 'Cases']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-600" />
            Case Reason Distribution
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={reasonChartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatNumber} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value?.length > 15 ? value.substring(0, 15) + '...' : value}
                />
                <Tooltip formatter={(value) => [formatNumber(value), 'Cases']} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {reasonChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* AI Analysis Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Top 20 Issue Patterns
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {patternsData ? (
                <>
                  AI-powered analysis of {formatNumber(patternsData.cases_fetched || patternsData.total_cases_analyzed)} cases
                  {patternsData.fallback_mode && <span className="text-yellow-600 ml-2">(SQL fallback mode)</span>}
                </>
              ) : (
                'Keyword-based pattern detection from case descriptions'
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!useAI && (
              <button
                onClick={handleRunAIAnalysis}
                disabled={aiLoading || aiFetching}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {aiLoading || aiFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4" />
                    Run AI Analysis
                  </>
                )}
              </button>
            )}
            {useAI && (
              <button
                onClick={() => refetchAI()}
                disabled={aiLoading || aiFetching}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${aiFetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
          </div>
        </div>

        {(aiLoading || aiFetching) && useAI && (
          <div className="flex flex-col items-center justify-center py-12 bg-purple-50 rounded-lg mb-6">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-4" />
            <p className="text-purple-700 font-medium">Analyzing case descriptions with AI...</p>
            <p className="text-purple-500 text-sm mt-1">This may take up to 2 minutes for comprehensive analysis</p>
          </div>
        )}

        {patternsData?.analysis_summary && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-amber-900 mb-2">Analysis Summary</h4>
            <p className="text-amber-700 text-sm">{patternsData.analysis_summary}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayPatterns.map((pattern, index) => (
            <PatternCard key={index} pattern={pattern} index={index} />
          ))}
        </div>

        {displayPatterns.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No patterns found. Try running AI analysis for more comprehensive results.</p>
          </div>
        )}
      </div>

      {/* Pattern Summary Table */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pattern Summary Table</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pattern</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Severity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayPatterns.map((pattern, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                      index < 3 ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {pattern.rank || index + 1}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {pattern.pattern_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                    {pattern.description}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    {formatNumber(pattern.estimated_count)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                    {pattern.percentage}%
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_COLORS[pattern.severity] || SEVERITY_COLORS.medium}`}>
                      {pattern.severity?.toUpperCase() || 'MEDIUM'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
